import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import {
  CheckCircle, ArrowForward, ExpandMore,
  Favorite, Email, Shield, AdminPanelSettings,
  DomainVerification, Palette, Groups, SupportAgent,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: 'Low', label: 'Per-Seat Cost' },
  { value: 'Full', label: 'Feature Access' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: 'Indian', label: 'Data Residency' },
];

const pillars = [
  {
    color: '#dc2626',
    icon: <Email sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Professional Image on a Tight Budget',
    desc: 'Move your team off @gmail and @yahoo onto your own domain — without the cost of a Workspace or Microsoft 365 subscription. Donors and partners see a credible organisation.',
    points: [
      'user@yourorg.org custom domain email',
      'Shared inboxes for info@, donate@, contact@',
      'White-label webmail with your branding',
      'No per-feature pricing surprises',
    ],
  },
  {
    color: '#7c3aed',
    icon: <AdminPanelSettings sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Easy Volunteer & Staff Management',
    desc: 'Add seasonal volunteers, remove departing staff, and reset passwords in seconds — no IT team needed. The admin panel is designed for non-technical administrators.',
    points: [
      'Add/remove users from any browser',
      'Suspend volunteers without deleting data',
      'Reset passwords instantly',
      'Set storage limits per user',
    ],
  },
  {
    color: '#059669',
    icon: <Shield sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Secure Communication You Can Trust',
    desc: 'Donor communications, grant applications, and beneficiary data deserve the same security as large organisations. TLS 1.3, DKIM/SPF/DMARC, 2FA, and spam filtering are all included.',
    points: [
      'TLS 1.3 encryption in transit',
      'Auto-configured SPF/DKIM/DMARC',
      'Rspamd spam and phishing filter',
      'Optional 2FA for all accounts',
    ],
  },
];

const features = [
  { icon: <DomainVerification sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'Custom Domain Email', desc: 'yourname@yourorg.org from day one — professional and trustworthy.' },
  { icon: <Palette sx={{ fontSize: 22, color: '#7c3aed' }} />, title: 'Branded Webmail', desc: 'Your logo, your colours — not CruvzMail branding, on every page your team sees.' },
  { icon: <Groups sx={{ fontSize: 22, color: '#0891b2' }} />, title: 'Volunteer-Friendly Access', desc: 'Temporary accounts for seasonal volunteers. Disable instantly when tenure ends.' },
  { icon: <Email sx={{ fontSize: 22, color: '#059669' }} />, title: 'Shared Inboxes & Aliases', desc: 'info@, volunteer@, grants@ — route to any staff member or shared inbox.' },
  { icon: <SupportAgent sx={{ fontSize: 22, color: '#d97706' }} />, title: 'Real Support Team', desc: 'Human support via email and chat. Response within 24 hours on all plans.' },
  { icon: <Favorite sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'No Hidden Fees', desc: 'Flat per-mailbox pricing. No add-ons, no surprise billing — predictable every month.' },
];

const faqs = [
  { q: 'Do you offer special pricing for non-profits?', a: 'Yes. We offer discounted per-mailbox rates for registered non-profit organisations (trusts, Section 8 companies, NGOs registered under FCRA or relevant state acts). Contact us with your registration details for a custom quote.' },
  { q: 'Can we have shared inboxes like info@ or donate@?', a: 'Yes. Shared inboxes and aliases are included on all plans. Set up info@yourorg.org, donate@yourorg.org, or any other address and route incoming mail to one or more staff members — no separate cost.' },
  { q: 'We have volunteers who join seasonally. How do we manage their accounts?', a: 'Add a volunteer account in seconds from the admin panel, with a temporary password. When their term ends, suspend the account (which blocks login but preserves emails) or delete it. You can also set account expiry dates in advance.' },
  { q: 'Our operations team is not technical. Is the admin panel easy to use?', a: 'Yes. The admin panel is designed for non-technical administrators. Adding users, resetting passwords, creating aliases, and setting storage limits all take a few clicks with clear labels — no server access or IT background needed.' },
  { q: 'Is our donor communication data stored safely in India?', a: 'Yes. All email data is stored in CruvzMail\'s own datacenter within India. We do not use international cloud providers. Your donor and beneficiary communication stays under Indian data jurisdiction.' },
];

const NonProfitPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout activeNav="For Business">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1a0000 50%, #0f172a 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -180, right: -120, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(5,150,105,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Non-Profit" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Professional Email for<br />Non-Profit Organisations
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 640, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Get your team on user@yourorg.org — with full admin control, donor-grade security, and predictable per-seat pricing that respects your operating budget.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#dc2626', boxShadow: '0 0 40px rgba(220,38,38,0.35)', '&:hover': { bgcolor: '#b91c1c' } }}>
              Ask About Non-Profit Pricing
            </Button>
            <Button onClick={() => navigate('/features')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Explore Features
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3.5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#fca5a5', lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500, mt: 0.5 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PILLARS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Why Non-Profits Choose CruvzMail</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Full-Featured Email That Respects Your Budget
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              The same features large organisations pay a premium for — at pricing designed for the third sector.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={p.title}>
                <Box sx={{
                  height: '100%', p: { xs: 3, md: 4 }, borderRadius: 3.5,
                  border: '1px solid #e2e8f0', bgcolor: '#fff', position: 'relative', overflow: 'hidden',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: p.color, boxShadow: `0 8px 40px ${p.color}12`, transform: 'translateY(-3px)' },
                }}>
                  <Typography sx={{ position: 'absolute', top: -14, right: 14, fontWeight: 900, fontSize: '5.5rem', color: `${p.color}09`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: `0 6px 20px ${p.color}30` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 1.2 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 2.5 }}>{p.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FEATURES ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 1 }}>
              Everything Your Organisation Needs
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>All features included — no add-ons required.</Typography>
          </Box>
          <Grid container spacing={2.5}>
            {features.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: '14px', height: '100%', bgcolor: '#fafafa', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' } }}>
                  <Box sx={{ mb: 1.5 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.6 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' } }}>Non-Profit FAQ</Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} elevation={0} disableGutters sx={{ border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden', '&:before': { display: 'none' }, '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: '#fecaca' } }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#64748b' }} />} sx={{ px: 3, py: 0.5, '&.Mui-expanded': { bgcolor: '#fffbfb' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', py: 0.5 }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, bgcolor: '#fffbfb' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.85 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 12 }, px: { xs: 2, md: 6 }, bgcolor: '#0f172a', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 620, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Your Mission Deserves<br />Professional Tools
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', mb: 5, lineHeight: 1.85 }}>
            Non-profit pricing available for registered organisations. Get in touch with your registration details.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
              Contact Us
            </Button>
            <Button onClick={() => navigate('/signup')} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              View Plans
            </Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default NonProfitPage;
